/**
 * tiers.js — Single source of truth for Helix subscription tiers.
 *
 * ── Generation costs at 1.5× markup ─────────────────────────────────────────
 * Formula: ceil(kieCredits × $0.005 × 1.5 / $0.01) = ceil(kieCredits × 0.75)
 *
 *   Image 1080P   →   5 credits  ($0.05)  — 6  kie cr/gen
 *   Image 2K      →   8 credits  ($0.08)  — 10 kie cr/gen
 *   Image 4K      →  14 credits  ($0.14)  — 18 kie cr/gen
 *   5s video 720P →  75 credits  ($0.75)  — 20 kie cr/sec × 5s
 *   5s video 1080P→ 102 credits  ($1.02)  — 27 kie cr/sec × 5s
 *   5s vid +audio →  113 credits ($1.13)  — 30 kie cr/sec × 5s
 *   5s vid 1080+a →  150 credits ($1.50)  — 40 kie cr/sec × 5s
 *   10s video 720P→  150 credits ($1.50)  — 20 kie cr/sec × 10s
 *   AI text       →   ~1 credit  per ~600 tokens (GPT-4o-mini, 8× markup)
 *
 * ── Storage (Cloudflare R2, no egress fees): $0.015 / GB / mo ────────────────
 *   Free       5 GB  → ~$0.075 / user / mo
 *   Creator   50 GB  → ~$0.75  / user / mo
 *   Pro      200 GB  → ~$3.00  / user / mo
 *   Studio   500 GB  → ~$7.50  / user / mo
 *   Team     500 GB  → ~$7.50  / team / mo (shared pool)
 *   Enterprise 1 TB  → ~$15    / team / mo
 *
 * ── Tier profitability at 50% credit utilisation (realistic SaaS) ────────────
 *   Free      $0   / ~$0.33 cost   → acquisition (CAC)
 *   Creator  $19   / ~$6.67 cost   → ~65% gross margin
 *   Pro      $49   / ~$16.67 cost  → ~66% gross margin
 *   Studio   $149  / ~$50.00 cost  → ~66% gross margin (15K credits)
 *   Team     $99+  / ~$44+ cost    → ~55%+ gross margin (per-seat pool)
 */

const GB = 1024 * 1024 * 1024;
const TB = GB * 1024;

// ── Storage limits ─────────────────────────────────────────────────────────────
export const STORAGE_LIMITS_BYTES = {
    free:       5   * GB,
    creator:    50  * GB,
    pro:        200 * GB,
    studio:     500 * GB,
    team:       500 * GB,
    enterprise: 1   * TB,
};

export const STORAGE_LIMIT_LABELS = {
    free:       '5 GB',
    creator:    '50 GB',
    pro:        '200 GB',
    studio:     '500 GB',
    team:       '500 GB',
    enterprise: '1 TB',
};

// ── Individual plans ───────────────────────────────────────────────────────────
export const INDIVIDUAL_TIERS = [
    {
        id:           'free',
        name:         'Free',
        monthlyPrice: 0,
        annualPrice:  0,
        annualTotal:  0,
        price:        '$0',
        period:       'forever',
        description:  'Build your first cinematic storyboard. No credit card required.',
        cta:          'Get Started Free',
        ctaPath:      '/signup',
        highlight:    false,
        badge:        null,
        credits:      100,
        tokens:       60000,     // ~60K AI tokens (100 cr × ~600 tok/cr)
        planType:     'individual',
        storageBytes: STORAGE_LIMITS_BYTES.free,
        storageLabel: STORAGE_LIMIT_LABELS.free,
        features: [
            '100 credits / month',
            '~20 keyframe generations (1080P)',
            '~1 video clip (5 sec, 720P)',
            '~60K AI writing tokens',
            '5 GB media storage',
            '1 active project',
            'Storyboard + timeline editor',
            'Community support',
        ],
    },
    {
        id:           'creator',
        name:         'Creator',
        monthlyPrice: 19,
        annualPrice:  16,     // $192 / yr  (saves $36) — 15% annual discount
        annualTotal:  192,
        price:        '$19',
        period:       'per month',
        description:  'For independent filmmakers and solo directors.',
        cta:          'Start Creating',
        ctaPath:      '/signup',
        highlight:    true,
        badge:        'Most Popular',
        credits:      2000,
        tokens:       1200000,  // ~1.2M AI tokens
        planType:     'individual',
        storageBytes: STORAGE_LIMITS_BYTES.creator,
        storageLabel: STORAGE_LIMIT_LABELS.creator,
        features: [
            '2,000 credits / month',
            '~190 keyframe generations (2K)',
            '~12 video clips (5 sec, Seedance 2 pro)',
            '~1.2M AI writing tokens',
            '50 GB media storage',
            'Unlimited projects',
            'Full node storyboard + timeline editor',
            'Objects library (characters, scenes, styles)',
            'Priority generation queue',
            'Email support',
        ],
    },
    {
        id:           'pro',
        name:         'Pro',
        monthlyPrice: 49,
        annualPrice:  42,     // $504 / yr  (saves $84) — 15% annual discount
        annualTotal:  504,
        price:        '$49',
        period:       'per month',
        description:  'For working professionals who ship consistently.',
        cta:          'Go Pro',
        ctaPath:      '/signup',
        highlight:    false,
        badge:        null,
        credits:      5000,
        tokens:       3000000,  // ~3M AI tokens
        planType:     'individual',
        storageBytes: STORAGE_LIMITS_BYTES.pro,
        storageLabel: STORAGE_LIMIT_LABELS.pro,
        features: [
            '5,000 credits / month',
            '~475 keyframe generations (2K)',
            '~30 video clips (5 sec, Seedance 2 pro)',
            '~3M AI writing tokens',
            '200 GB media storage',
            'Unlimited projects',
            'Everything in Creator',
            'Brand assets library',
            'Custom style presets',
            'API access',
            'Priority support',
        ],
    },
    {
        id:           'studio',
        name:         'Studio',
        monthlyPrice: 149,
        annualPrice:  127,    // $1,524 / yr  (saves $264) — 15% annual discount
        annualTotal:  1524,
        price:        '$149',
        period:       'per month',
        description:  'For power creators and boutique studios who demand the most.',
        cta:          'Go Studio',
        ctaPath:      '/signup',
        highlight:    false,
        badge:        'Best Value',
        credits:      15000,
        tokens:       9000000,  // ~9M AI tokens
        planType:     'individual',
        storageBytes: STORAGE_LIMITS_BYTES.studio,
        storageLabel: STORAGE_LIMIT_LABELS.studio,
        features: [
            '15,000 credits / month',
            '~1,425 keyframe generations (2K)',
            '~88 video clips (5 sec, Seedance 2 pro)',
            '~9M AI writing tokens',
            '500 GB media storage',
            'Unlimited projects',
            'Everything in Pro',
            'White-label exports',
            'Advanced usage analytics',
            'Dedicated priority support',
        ],
    },
];

// ── Team plans ─────────────────────────────────────────────────────────────────
//
// Team uses per-seat pricing above a 3-seat base.
// Compute price in components using TEAM_SEAT_PRICING helpers below.
export const TEAM_TIERS = [
    {
        id:                  'team',
        name:                'Team',
        // Base prices (for MIN_TEAM_SEATS = 3 seats)
        monthlyPrice:        99,   // base at 3 seats
        annualPrice:         84,   // base at 3 seats, monthly equivalent — 15% annual discount
        annualTotal:         1008, // base at 3 seats, billed annually
        price:               '$99',
        period:              'per month',
        description:         'Shared credit pool for collaborative production teams.',
        cta:                 'Start Free Trial',
        ctaPath:             '/signup',
        highlight:           true,
        badge:               'Most Popular',
        // Per-seat costs above the base (3 seats)
        pricePerSeat:        29,   // $/mo per seat above 3 (monthly billing)
        annualPricePerSeat:  25,   // $/mo per seat above 3 (annual billing, monthly equiv.) — 15% off
        // Credit pool scaling
        baseCredits:         10000, // credits at 3 seats
        creditsPerSeat:      3500,  // extra credits per seat above 3
        baseTokens:          6000000,
        tokensPerSeat:       2100000,
        // Seat bounds
        minSeats:            3,
        maxSeats:            15,
        seats:               3,    // default display
        planType:            'team',
        storageBytes:        STORAGE_LIMITS_BYTES.team,
        storageLabel:        STORAGE_LIMIT_LABELS.team,
        features: [
            'Shared credit pool',
            '~10K credits base + 3,500/seat',
            'Shared 500 GB media storage',
            'Everything in Studio',
            'Team credit dashboard',
            'Roles & permissions',
            'Dedicated account manager',
        ],
    },
    {
        id:           'enterprise',
        name:         'Enterprise',
        monthlyPrice: null,
        annualPrice:  null,
        annualTotal:  null,
        price:        'Custom',
        period:       'contact us',
        description:  'Custom infrastructure for large studios and networks.',
        cta:          'Contact Sales',
        ctaPath:      '/contact',
        highlight:    false,
        badge:        null,
        credits:      null,
        tokens:       null,
        seats:        null,
        planType:     'team',
        storageBytes: STORAGE_LIMITS_BYTES.enterprise,
        storageLabel: STORAGE_LIMIT_LABELS.enterprise,
        features: [
            'Custom credit allocation',
            'Custom storage (1 TB+)',
            'Unlimited seats',
            'Everything in Team',
            'SSO / SAML',
            'Custom model fine-tuning',
            'On-premise deployment',
            'SLA & dedicated CSM',
        ],
    },
];

// ── Team seat pricing helpers ──────────────────────────────────────────────────
export const TEAM_SEAT_PRICING = {
    minSeats:           3,
    maxSeats:           15,
    // Monthly billing
    baseMonthly:        99,   // price for 3 seats
    perSeatMonthly:     29,   // per additional seat above 3
    // Annual billing (monthly equivalent shown to user) — 15% discount
    baseAnnual:         84,   // price for 3 seats
    perSeatAnnual:      25,   // per additional seat above 3
    // Credits
    baseCredits:        10000,
    creditsPerSeat:     3500,
};

/** Compute the displayed monthly price for a given seat count + billing period */
export const computeTeamPrice = (seats, billing) => {
    const extra = Math.max(0, seats - TEAM_SEAT_PRICING.minSeats);
    if (billing === 'annual') {
        return TEAM_SEAT_PRICING.baseAnnual + extra * TEAM_SEAT_PRICING.perSeatAnnual;
    }
    return TEAM_SEAT_PRICING.baseMonthly + extra * TEAM_SEAT_PRICING.perSeatMonthly;
};

/** Compute the annual total for a given seat count */
export const computeTeamAnnualTotal = (seats) => computeTeamPrice(seats, 'annual') * 12;

/** Compute credits for a given seat count */
export const computeTeamCredits = (seats) => {
    const extra = Math.max(0, seats - TEAM_SEAT_PRICING.minSeats);
    return TEAM_SEAT_PRICING.baseCredits + extra * TEAM_SEAT_PRICING.creditsPerSeat;
};

// ── TIERS — all individual plans (backward-compatible) ────────────────────────
// Includes Free → Creator → Pro → Studio → Enterprise.
// Team tier is handled separately via TEAM_TIERS (per-seat pricing shown on own tab).
export const TIERS = [
    ...INDIVIDUAL_TIERS,
    {
        ...TEAM_TIERS[1], // Enterprise
        planType: 'individual', // treat as the "top" tier for Account.jsx ordering
    },
];

// ── Accurate generation cost reference ────────────────────────────────────────
// Used in Account.jsx "Credits" tab.  Reflects actual 1.5× markup prices.
export const GENERATION_COSTS = [
    { action: 'Keyframe — 1080P',        credits: 5,   cost: '$0.05', model: 'Nano Banana 2 Pro' },
    { action: 'Keyframe — 2K',           credits: 8,   cost: '$0.08', model: 'Nano Banana 2 Pro' },
    { action: 'Keyframe — 4K',           credits: 14,  cost: '$0.14', model: 'Nano Banana 2 Pro' },
    { action: '5s Video Clip — 720P',    credits: 75,  cost: '$0.75', model: 'Kling 3.0'         },
    { action: '5s Video Clip — 1080P',   credits: 102, cost: '$1.02', model: 'Kling 3.0'         },
    { action: '5s Clip + AI Audio',      credits: 113, cost: '$1.13', model: 'Kling 3.0 Audio'   },
    { action: '5s Clip — 1080P + Audio', credits: 150, cost: '$1.50', model: 'Kling 3.0 Audio'   },
    { action: '10s Video Clip — 720P',   credits: 150, cost: '$1.50', model: 'Kling 3.0'         },
    { action: '10s Video Clip — 1080P',  credits: 203, cost: '$2.03', model: 'Kling 3.0'         },
    { action: 'AI Text / Script',        credits: 1,   cost: '$0.01', model: 'GPT-4o-mini (~600 tokens)' },
];

// ── Credit packs — top-up bundles purchased à-la-carte ────────────────────────
export const CREDIT_PACKS = [
    {
        id:       'starter',
        label:    'Starter',
        credits:  500,
        price:    7,
        discount: null,
        popular:  false,
    },
    {
        id:       'creator',
        label:    'Creator',
        credits:  2_000,
        price:    22,
        discount: '10%',
        popular:  false,
    },
    {
        id:       'pro',
        label:    'Pro',
        credits:  5_000,
        price:    49,
        discount: '17%',
        popular:  true,
    },
    {
        id:       'studio',
        label:    'Studio',
        credits:  15_000,
        price:    129,
        discount: '25%',
        popular:  false,
    },
];

// ── Founding 50 program ──────────────────────────────────────────────────────
//
// First 50 subscribers from the waitlist get 50% off for life.
// Implemented via Stripe coupon (FOUNDING50) applied at checkout.
//
// ── Worst-case unit economics (verified 2026-05-06) ─────────────────────────
//
// "Max heavy Seedance utilization" — every credit spent on Seedance 2 pro 5s
// clips, the most expensive video tier we offer. KIE charges $0.55 per clip;
// we bill 168.75 credits per clip (2.5× markup on $0.135/sec base). Per-user
// fixed overhead amortizes Atlas M10 + Continuous Backup ($67/mo / 50 founders
// = $1.34) plus Stripe (2.9% + $0.30) plus storage worst-case (full quota).
//
// Founding @ MAX heavy-Seedance util:
//   Creator  $9.50  → ~11.85 clips × $0.55 = $6.52 KIE + $2.67 overhead = $9.19  → +$0.31  (break-even floor)
//   Pro      $24.50 → ~29.63 clips × $0.55 = $16.30 KIE + $5.35 overhead = $21.65 → +$2.85  (12% margin)
//   Studio   $74.50 → ~88.89 clips × $0.55 = $48.89 KIE + $11.30 overhead = $60.19 → +$14.31 (19% margin)
//
// Founding @ realistic 30% mixed util:
//   Creator  $9.50  → ~$5.50 cost → +$4.00  (~42% margin)
//   Pro      $24.50 → ~$13.50 cost → +$11.00 (~45% margin)
//   Studio   $74.50 → ~$36.00 cost → +$38.50 (~52% margin)
//
// Full-price (post-Founding) @ MAX heavy-Seedance:
//   Creator  $19  → +$9.54  (50% margin)
//   Pro      $49  → +$26.75 (55% margin)
//   Studio   $149 → +$87.07 (58% margin)
//
// ── Why these specific markups ──────────────────────────────────────────────
// Seedance 2 / Kling 3 / Kling Motion Control all run at 2.5× markup on
// $0.135/sec base. 2.5× was the exact floor that gets Founding Creator to
// break-even at 100% credit utilization on the heaviest model — anything
// lower and we lose money on max-util Founding Creators. Seedance 2 Fast
// stays at 2.0× (cheaper KIE base + value-tier positioning).
//
// Image markups: Nano Banana Pro 2.0× (KIE $0.030 → 5cr ≈ $0.05, 40% margin),
// Nano Banana 2 1.5× (KIE $0.045 → 10.5cr ≈ $0.105, 57% margin), GPT Image 2
// 1.5× (KIE $0.167 → 25cr ≈ $0.25, 33% margin — was previously losing money
// when seed had base=$0.053, fixed 2026-05-06 to match KIE high-quality cost).

export const FOUNDING_CONFIG = {
    enabled:       true,
    maxMembers:    50,
    discountPct:   50,         // 50% off
    duration:      'forever',  // locked in for life
    stripeCoupon:  'FOUNDING50',
    label:         'Founding Member',
    badge:         '\uD83D\uDD25 Founding 50',
    tagline:       '50% off for life \u2014 only 50 spots',
};

export const FOUNDING_PRICES = {
    creator: {
        monthly:     9.50,     // $19 x 0.5
        annual:      8.00,     // $16 x 0.5  ($96/yr)
        annualTotal: 96,
    },
    pro: {
        monthly:     24.50,    // $49 x 0.5
        annual:      21.00,    // $42 x 0.5  ($252/yr)
        annualTotal: 252,
    },
    studio: {
        monthly:     74.50,    // $149 x 0.5
        annual:      63.50,    // $127 x 0.5  ($762/yr)
        annualTotal: 762,
    },
};

/** Check if a tier has founding pricing available */
export const hasFoundingPrice = (tierId) =>
    FOUNDING_CONFIG.enabled && tierId in FOUNDING_PRICES;

/** Get founding price for a tier + billing period */
export const getFoundingPrice = (tierId, billing = 'monthly') => {
    const fp = FOUNDING_PRICES[tierId];
    if (!fp) return null;
    return billing === 'annual' ? fp.annual : fp.monthly;
};

/** Get founding savings vs regular price */
export const getFoundingSavings = (tierId, billing = 'monthly') => {
    const tier = INDIVIDUAL_TIERS.find((t) => t.id === tierId);
    const fp = FOUNDING_PRICES[tierId];
    if (!tier || !fp) return 0;
    const regular = billing === 'annual' ? tier.annualPrice : tier.monthlyPrice;
    const founding = billing === 'annual' ? fp.annual : fp.monthly;
    return regular - founding;
};

// ── Helpers ────────────────────────────────────────────────────────────────────

export const getStorageLimitBytes = (tier) =>
    STORAGE_LIMITS_BYTES[tier] ?? STORAGE_LIMITS_BYTES.free;

export const getStorageLimitLabel = (tier) =>
    STORAGE_LIMIT_LABELS[tier] ?? STORAGE_LIMIT_LABELS.free;

export const formatBytes = (bytes, decimals = 1) => {
    if (!bytes || bytes <= 0) return '0 B';
    const k     = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i     = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};
